import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Star } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/utils';

export const ProductCard = ({ product }) => {
  const { addToWishlist, removeFromWishlist, isInWishlist } = useApp();
  const inWishlist = isInWishlist(product.id);

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product.id);
    }
  };

  const hasRange = product.price_to > product.price_from;
  const isOnSale = (product.variants || []).some((v) => v.sale_price && v.sale_price < v.price);

  return (
    <Link 
      to={`/product/${product.id}`} 
      className="product-card group block bg-white rounded-xl overflow-hidden border border-border/50"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[3/4] overflow-hidden bg-muted">
        {isOnSale && (
          <span className="sale-badge">Sale</span>
        )}
        <button
          onClick={handleWishlistClick}
          className={`wishlist-btn ${inWishlist ? 'active' : ''}`}
          data-testid={`wishlist-btn-${product.id}`}
          aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Heart className={`h-4 w-4 ${inWishlist ? 'fill-current' : ''}`} />
        </button>
        <img
          src={product.images?.[0] || 'https://images.unsplash.com/photo-1559709451-1b5dc3c0038c?w=400'}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">
          {product.category}
        </p>
        <h3 className="font-medium text-foreground mb-2 line-clamp-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          {product.name}
        </h3>
        
        {/* Rating */}
        {product.review_count > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-3 w-3 ${
                    star <= product.avg_rating 
                      ? 'fill-amber-400 text-amber-400' 
                      : 'text-slate-200'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">({product.review_count})</span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-center gap-2">
          {hasRange && <span className="text-xs text-muted-foreground">from</span>}
          <span className="font-semibold text-[#EA580C]">
            {formatPrice(product.price_from)}
          </span>
          {isOnSale && !hasRange && (
            <span className="sale-badge-inline text-xs text-green-600">On sale</span>
          )}
        </div>

        {/* Stock Status */}
        {product.total_stock <= 5 && product.total_stock > 0 && (
          <p className="text-xs text-amber-600 mt-2">Only {product.total_stock} left</p>
        )}
        {product.total_stock === 0 && (
          <p className="text-xs text-red-600 mt-2">Out of stock</p>
        )}
      </div>
    </Link>
  );
};
